/**
 * B6 · RUTAS DE PROGRESIÓN — espejo TS del motor Rust
 * (`packages/core-rust/src/progression`).
 *
 * El SERVIDOR es la autoridad: el estado de cada etapa, el certificado y la
 * prueba de nivelación se calculan allí. Este espejo cubre lo que la interfaz
 * necesita sin pedir permiso al servidor:
 *   • el vocabulario (qué scopes existen, cómo se llama un «nivel» aquí);
 *   • el grafo de prerrequisitos DERIVADO, para dibujar el camino;
 *   • los manifests `academy.progression.scope.<key>` del catálogo de features.
 *
 * Mantener EN SYNC con Rust: un scope nuevo se añade en ambos (hay un test de
 * paridad que compara las dos listas).
 *
 * ## Por qué el código dice `stage` y no `level`
 *
 * `packages/shared/src/gamification/xp.ts` ya usa «nivel» para el XP. Reusar la
 * palabra para el currículo garantizaría confusión permanente entre una curva
 * motivacional (apagable, no autoritativa) y algo que certifica y cobra. En el
 * código son etapas; la ETIQUETA que ve el alumno la decide la configuración.
 */

import type { FeatureManifest } from "../country/packs";
import type { CompletionConfig, CompletionNode } from "../completion/registry";

// ── Orígenes de etapas (== Rust `progression::scopes`) ────────────────────────

export const PROGRESSION_SCOPES = [
  "program_course",
  "program_chain",
  "course_unit",
  "manual",
] as const;

export type ProgressionScopeKey = (typeof PROGRESSION_SCOPES)[number];

/** El default del dueño: **un nivel es un curso dentro de un programa**. */
export const DEFAULT_SCOPE_KEY: ProgressionScopeKey = "program_course";

export interface ProgressionScopeMeta {
  key: ProgressionScopeKey;
  /** Qué ES un nivel con este origen (frase que ve quien configura). */
  name: string;
  description: string;
  /** Params que pide, con su etiqueta para el editor. */
  params: { key: string; label: string; type: "text" | "list" | "boolean" }[];
  /** `true` si el reconcile genera etapas (el manual no genera nada). */
  generates: boolean;
}

export const PROGRESSION_SCOPES_META: Record<ProgressionScopeKey, ProgressionScopeMeta> = {
  program_course: {
    key: "program_course",
    name: "Un nivel = un curso del programa",
    description:
      "Cada curso del programa, en su orden, es un nivel. Es la configuración por defecto: añadir un curso al programa añade un nivel sin tocar nada más.",
    params: [
      {
        key: "required_only",
        label: "Solo los cursos troncales son niveles",
        type: "boolean",
      },
    ],
    generates: true,
  },
  program_chain: {
    key: "program_chain",
    name: "Un nivel = un programa entero",
    description:
      "Una cadena de programas (por ejemplo A1 → A2 → B1), cada uno con sus propios cursos. La ruta recorre varios programas en vez de colgar de uno.",
    params: [{ key: "program_ids", label: "Programas en orden", type: "list" }],
    generates: true,
  },
  course_unit: {
    key: "course_unit",
    name: "Un nivel = una unidad del curso",
    description:
      "Cada unidad de un curso es un nivel. Pensado para talleres libres a tu ritmo, sin cohorte ni certificado por nivel.",
    params: [{ key: "course_id", label: "Curso", type: "text" }],
    generates: true,
  },
  manual: {
    key: "manual",
    name: "Los niveles los defines tú",
    description:
      "La academia crea cada nivel a mano y puede mezclar orígenes: uno anclado a un curso, otro a una cohorte, otro sin contenido que solo entrega el certificado final. La reconciliación no toca nada.",
    params: [],
    generates: false,
  },
};

/** Manifests auto-descriptivos de los orígenes (`academy.progression.scope.<key>`). */
export function progressionScopeManifests(): FeatureManifest[] {
  return PROGRESSION_SCOPES.map((key) => ({
    key: `academy.progression.scope.${key}`,
    name: PROGRESSION_SCOPES_META[key].name,
    description: PROGRESSION_SCOPES_META[key].description,
    scope: "org" as const,
    status: "beta" as const,
    requires: ["academy.progression"],
  }));
}

// ── Etiqueta del nivel ────────────────────────────────────────────────────────

export const STAGE_LABELS = ["level", "module", "cycle", "term", "stage", "custom"] as const;
export type StageLabel = (typeof STAGE_LABELS)[number];

/**
 * Configuración efectiva de una ruta (== Rust `ProgressionConfig`).
 * La cascada `plataforma → organización → ruta` la resuelve el servidor; aquí
 * solo se lee lo que llega.
 */
export interface ProgressionConfig {
  scopeKey: ProgressionScopeKey | string;
  stageLabel: StageLabel;
  stageLabelCustom?: string | null;
  params?: Record<string, unknown>;
  defaultUnlock?: CompletionConfig | null;
  defaultOnComplete?: unknown[];
  nodeSource: string;
  milestoneEvery: number;
}

/** Los mismos valores que `ProgressionConfig::platform_default()` en Rust. */
export const PLATFORM_DEFAULT_CONFIG: ProgressionConfig = {
  scopeKey: DEFAULT_SCOPE_KEY,
  stageLabel: "level",
  params: {},
  nodeSource: "class_session",
  milestoneEvery: 8,
};

// ── Estado de una etapa ───────────────────────────────────────────────────────

export const STAGE_STATUSES = [
  "locked",
  "available",
  "in_progress",
  "completed",
  "certified",
] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

export interface StageState {
  stageId: string;
  stageKey: string;
  position: number;
  title: string;
  anchorKind: string;
  anchorId?: string | null;
  status: StageStatus;
  /** Key del criterio que bloquea — el motivo, no un candado mudo. */
  blockedBy?: string | null;
  progressPct: number;
  unlockBreakdown?: {
    key: string;
    met: boolean;
    current: number;
    target: number;
    detail: string;
  }[];
}

export interface PrerequisiteEdge {
  fromStageKey: string;
  toStageKey: string;
  /** `false` = hay una vía alternativa (así se dibuja el salto de nivel). */
  hard: boolean;
}

export interface TrackState {
  trackId: string;
  trackName: string;
  stageLabel: StageLabel;
  stageLabelCustom?: string | null;
  stages: StageState[];
  edges: PrerequisiteEdge[];
  /** `false` cuando los prerrequisitos están apagados: todo disponible. */
  prerequisitesEnforced: boolean;
}

// ── Grafo DERIVADO (espejo de `progression::prerequisite_edges`) ──────────────

export interface StageRef {
  stageKey: string;
  position: number;
  unlock?: CompletionConfig | null;
}

/**
 * DERIVA las aristas de prerrequisito de los árboles de desbloqueo.
 *
 * No hay tabla de aristas a propósito: sería una segunda fuente de verdad
 * frente a `unlock_config` y se desincronizaría a la primera edición. Esta
 * función no puede desincronizarse, y se testea sin servidor.
 */
export function prerequisiteEdges(stages: StageRef[]): PrerequisiteEdge[] {
  const ordered = [...stages].toSorted((a, b) =>
    a.position === b.position ? (a.stageKey < b.stageKey ? -1 : 1) : a.position - b.position,
  );
  const edges: PrerequisiteEdge[] = [];

  const walk = (
    node: CompletionNode,
    hard: boolean,
    stage: StageRef,
    previous: StageRef | undefined,
  ): void => {
    if (node.type === "all") {
      for (const child of node.children) {
        walk(child, hard, stage, previous);
      }
      return;
    }
    if (node.type === "any") {
      // Bajo un `any` hay alternativa ⇒ la arista deja de ser dura.
      for (const child of node.children) {
        walk(child, false, stage, previous);
      }
      return;
    }
    if (node.key !== "stage_completed") {
      return;
    }
    const params = node.params ?? {};
    const from =
      params.previous === true
        ? previous?.stageKey
        : typeof params.stage_key === "string"
          ? params.stage_key
          : undefined;
    // Una etapa no es prerrequisito de sí misma: un ciclo colgaría al camino.
    if (from === undefined || from === stage.stageKey) {
      return;
    }
    edges.push({ fromStageKey: from, toStageKey: stage.stageKey, hard });
  };

  ordered.forEach((stage, index) => {
    if (!stage.unlock) {
      return;
    }
    walk(stage.unlock.root, true, stage, index > 0 ? ordered[index - 1] : undefined);
  });
  return edges;
}

// ── Camino visual: nodos e hitos ─────────────────────────────────────────────

export interface PathNode {
  nodeKey: string;
  position: number;
  title: string;
  /** Hito (fin de mes): forma distinta y celebración propia. */
  milestone: boolean;
}

/**
 * Numera los nodos de una etapa y marca el hito cada `milestoneEvery`
 * (8 por defecto = 4 semanas × 2 clases = **fin de mes**). Espejo exacto de
 * `progression::default_nodes`. `0` apaga los hitos.
 */
export function pathNodes(
  items: { id: string; title: string }[],
  milestoneEvery: number,
): PathNode[] {
  return items.map((item, index) => ({
    nodeKey: item.id,
    position: index,
    title: item.title,
    // El hito CIERRA el bloque (el 8.º de cada 8), no lo abre.
    milestone: milestoneEvery > 0 && (index + 1) % milestoneEvery === 0,
  }));
}
